/**
 * Console pattern: general/Transport — 传输配置.
 *
 * Spec §5.6. Three transport types (local / relay_http / email), conditional
 * subforms, save + test-connection, and a current-config preview.
 *
 * Static prototype: state is local, "测试连接" returns a canned success after
 * a brief artificial delay so the empty/success/error tone is demoable.
 */

import { useState } from "react";
import {
  Cable,
  CheckCircle2,
  Cpu,
  Globe2,
  Mail,
  Save,
  Zap,
} from "lucide-react";

import {
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "../shells/console-shell";
import { cn } from "@/app/components/ui/utils";

type TransportType = "local" | "relay_http" | "email";

const TRANSPORTS: {
  key: TransportType;
  label: string;
  icon: typeof Cable;
  oneLine: string;
  pros: string[];
  cons: string[];
}[] = [
  {
    key: "local",
    label: "Local · stdio",
    icon: Cpu,
    oneLine: "Caller 与 Responder 跑在同一台机器，进程间 stdio 通信。",
    pros: ["最快、零依赖", "不暴露任何端口", "适合日常开发与单机自用"],
    cons: ["仅限同机", "Caller 与 Responder 必须共启停"],
  },
  {
    key: "relay_http",
    label: "Relay HTTP",
    icon: Globe2,
    oneLine: "通过 Relay 服务跨机/跨网调用，HTTP + 信令长连接。",
    pros: ["跨机器", "支持多 Caller 共享 Responder", "可以跑在 NAT 后"],
    cons: ["需要 Relay 服务可达", "增加一跳延迟"],
  },
  {
    key: "email",
    label: "Email",
    icon: Mail,
    oneLine: "回退方案：通过邮件信道异步投递调用与结果（实验性）。",
    pros: ["不需要任何端口/Relay", "审计天然可追溯"],
    cons: ["延迟 30s ~ 5min", "不适合实时调用", "受邮件 provider 限速"],
  },
];

export function ConsolePageTransport() {
  const [active, setActive] = useState<TransportType>("local");
  const [relayUrl, setRelayUrl] = useState("https://relay.delegated-execution.dev");
  const [emailProvider, setEmailProvider] = useState<"gmail" | "smtp">("gmail");
  const [sender, setSender] = useState("ops-bot@my-company.com");
  const [receiver, setReceiver] = useState("ops-bot@my-company.com");
  const [testState, setTestState] = useState<"idle" | "running" | "ok" | "fail">("idle");
  const [savedConfig, setSavedConfig] = useState<TransportType>("local");

  const runTest = () => {
    setTestState("running");
    setTimeout(() => setTestState("ok"), 700);
  };

  return (
    <>
      <ConsolePageHeader
        kicker="GENERAL · 传输配置"
        title="选一种 Caller ↔ Responder 的通信方式"
        description="不同部署形态走不同信道。原型仅展示形态，不会真的写文件；保存后底部「当前配置」会同步。"
      />

      {/* 1. 通信类型说明卡（3 选 1） */}
      <ConsoleCard className="mb-5">
        <p className="text-[12px] font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">
          通信类型 · 三选一
        </p>
        <div className="grid grid-cols-3 gap-3">
          {TRANSPORTS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={cn(
                  "text-left p-3 border bg-white transition-shadow",
                  isActive
                    ? "c-ink-border-2 c-shadow-1"
                    : "border-[var(--border)] hover:border-[var(--brand-muted)]",
                )}
                style={{ borderRadius: "var(--c-radius)" }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="h-4 w-4" strokeWidth={2.5} />
                  <span className="text-[13px] font-semibold">{t.label}</span>
                  {savedConfig === t.key && (
                    <span
                      className="ml-auto c-status c-status-success text-[9.5px]"
                      title="当前已生效配置"
                    >
                      已生效
                    </span>
                  )}
                </div>
                <p className="text-[11.5px] text-[var(--brand-muted)] leading-snug">
                  {t.oneLine}
                </p>
                <ul className="mt-2 space-y-0.5">
                  {t.pros.map((p) => (
                    <li
                      key={p}
                      className="text-[10.5px] text-[var(--c-status-success-fg)] flex items-start gap-1"
                    >
                      <span className="leading-none">+</span>
                      <span className="text-[var(--ink)]/80 leading-tight">{p}</span>
                    </li>
                  ))}
                  {t.cons.map((c) => (
                    <li
                      key={c}
                      className="text-[10.5px] text-[var(--c-status-warn-fg)] flex items-start gap-1"
                    >
                      <span className="leading-none">−</span>
                      <span className="text-[var(--ink)]/80 leading-tight">{c}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </ConsoleCard>

      {/* 2. 子表单（按 active 条件渲染） */}
      <ConsoleCard className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[12px] font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
            {active === "local" && "Local · 无需额外配置"}
            {active === "relay_http" && "Relay HTTP · 配置"}
            {active === "email" && "Email · 配置"}
          </p>
          <span className="font-mono text-[11px] text-[var(--brand-muted)]">
            POST /runtime/transport
          </span>
        </div>

        {active === "local" && (
          <div className="text-[12.5px] text-[var(--brand-muted)] flex items-start gap-2 leading-relaxed">
            <Cpu className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
            <p>
              Caller 与 Responder 自动通过 stdio 直连。进程由 supervisor 共同管理，
              重启时一同停止。<strong className="text-[var(--ink)]">无需输入。</strong>
            </p>
          </div>
        )}

        {active === "relay_http" && (
          <div className="space-y-3">
            <FormRow label="Relay base_url" hint="必须可达的 HTTPS 地址；保存前会通过「测试连接」验证。">
              <input
                type="text"
                value={relayUrl}
                onChange={(e) => setRelayUrl(e.target.value)}
                className="w-full px-2.5 py-1.5 border bg-white text-[12.5px] font-mono"
                style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
                placeholder="https://relay.delegated-execution.dev"
              />
            </FormRow>
            <FormRow label="鉴权" hint="原型中不展示真实 token；ops-console 会从 keychain 读取。">
              <span className="text-[12px] text-[var(--brand-muted)] font-mono">
                ********  <span className="text-[10.5px]">(从 keychain · 不可在此修改)</span>
              </span>
            </FormRow>
          </div>
        )}

        {active === "email" && (
          <div className="space-y-3">
            <FormRow label="Provider">
              <div className="flex gap-2">
                {(["gmail", "smtp"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setEmailProvider(p)}
                    className={cn(
                      "px-3 py-1 text-[12px] font-semibold border bg-white",
                      emailProvider === p
                        ? "c-ink-border bg-[var(--brand-secondary)]"
                        : "border-[var(--border)] text-[var(--brand-muted)]",
                    )}
                    style={{ borderRadius: "var(--c-radius-sm)" }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </FormRow>
            <FormRow label="Sender">
              <input
                type="text"
                value={sender}
                onChange={(e) => setSender(e.target.value)}
                className="w-full px-2.5 py-1.5 border bg-white text-[12.5px] font-mono"
                style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
              />
            </FormRow>
            <FormRow label="Receiver">
              <input
                type="text"
                value={receiver}
                onChange={(e) => setReceiver(e.target.value)}
                className="w-full px-2.5 py-1.5 border bg-white text-[12.5px] font-mono"
                style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
              />
            </FormRow>
          </div>
        )}

        <div className="mt-5 pt-4 border-t flex items-center gap-2.5" style={{ borderColor: "var(--border)" }}>
          <ConsoleButton
            variant="primary"
            onClick={() => setSavedConfig(active)}
            disabled={savedConfig === active && testState !== "ok"}
          >
            <Save className="h-3.5 w-3.5" />
            保存
          </ConsoleButton>
          <ConsoleButton variant="ghost" onClick={runTest} disabled={active === "local"}>
            <Zap className="h-3.5 w-3.5" />
            测试连接
          </ConsoleButton>
          {testState === "running" && (
            <span className="text-[12px] text-[var(--brand-muted)]">testing…</span>
          )}
          {testState === "ok" && (
            <span className="c-status c-status-success">
              <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
              连接成功 · 12 ms
            </span>
          )}
          {testState === "fail" && (
            <span className="c-status c-status-error">连接失败</span>
          )}
        </div>
      </ConsoleCard>

      {/* 3. 当前配置预览 */}
      <ConsoleCard padded={false} className="overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <Cable className="h-4 w-4" strokeWidth={2.5} />
            <p className="text-[13px] font-semibold">当前配置 · 即时生效</p>
          </div>
          <span className="font-mono text-[10.5px] text-[var(--brand-muted)]">
            ~/.delexec/transport.json
          </span>
        </div>
        <pre className="px-5 py-3 text-[11.5px] font-mono text-[var(--ink)] bg-[var(--brand-secondary)]/30 m-0 overflow-x-auto leading-relaxed">
{`{
  "transport": "${savedConfig}"${
    savedConfig === "relay_http"
      ? `,
  "relay": {
    "base_url": ${JSON.stringify(relayUrl)}
  }`
      : ""
  }${
    savedConfig === "email"
      ? `,
  "email": {
    "provider": "${emailProvider}",
    "sender": ${JSON.stringify(sender)},
    "receiver": ${JSON.stringify(receiver)}
  }`
      : ""
  }
}`}
        </pre>
      </ConsoleCard>
    </>
  );
}

function FormRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
      <div>
        <p className="text-[12px] font-semibold text-[var(--ink)] pt-1.5">{label}</p>
        {hint && (
          <p className="text-[10.5px] text-[var(--brand-muted)] leading-tight mt-0.5">{hint}</p>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}
